import _ from "lodash";
import React from "react";
import classNames from "classnames";
import { useI18n } from "Hooks";
import classes from "./ReplyContainerV2.scss";
import { Avatar, Tooltip } from "@toddle-edu/ds-web";
import {
  ATTACHMENT_TYPES,
  INTERACTION_TYPE,
  MESSAGE_TYPE,
  getFormattedNameForUserType,
  getIsMessagingWidget,
  getIsSidePaneMode,
} from "MessagingV2/modules/Utils";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { getAvatarColor } from "Utils";
import { getCommentDisplayContent } from "Utils/RegexHelper";
import { AttachmentList } from "Attachment";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { connect } from "react-redux";
import { PageFoldPortraitOutlined, PollOutlined } from "@toddle-edu/ds-icons";
import ConditionalWrapper from "Utils/ConditionalWrapper";

const getAttachmentGrid = ({ noOfAttachments }) => {
  return {
    gridTemplateColumns: `repeat(${noOfAttachments} , 200px)`,
    gridGap: "8px",
  };
};

const QUESTION_TEXT_LENGTH = 20;

const InteractiveAttachmentContainer = props => {
  const { attachment } = props;

  const interaction = _.get(attachment, "interaction", {});

  const type = _.get(attachment, "type", null);

  switch (type) {
    case INTERACTION_TYPE.POLL: {
      const question = _.get(interaction, "question", "");

      return (
        <div className={classes.interactiveMessageContainer}>
          <PollOutlined />

          {question ? (
            <ConditionalWrapper
              wrapper={<Tooltip tooltip={question} />}
              condition={_.size(question) > QUESTION_TEXT_LENGTH}
              type={"wrapperAsElement"}
            >
              <div className="text-body">
                {_.truncate(question, { length: QUESTION_TEXT_LENGTH })}
              </div>
            </ConditionalWrapper>
          ) : null}
        </div>
      );
    }

    case INTERACTION_TYPE.FORM: {
      const title = _.get(interaction, "title", "");
      return (
        <div className={classes.interactiveMessageContainer}>
          <PageFoldPortraitOutlined />
          {title ? (
            <ConditionalWrapper
              wrapper={<Tooltip tooltip={title} />}
              condition={_.size(title) > QUESTION_TEXT_LENGTH}
              type={"wrapperAsElement"}
            >
              <div className="text-body">
                {_.truncate(title, { length: QUESTION_TEXT_LENGTH })}
              </div>
            </ConditionalWrapper>
          ) : null}
        </div>
      );
    }
    default:
      return null;
  }
};

const ForwardMessageAttachmentContainer = props => {
  const { attachments = [] } = props;
  const noOfAttachments = _.size(attachments);

  if (!noOfAttachments) return null;

  return (
    <div className={classes.forwardMessageAttachmentsContainer}>
      <AttachmentList
        value={attachments}
        mode="view"
        mediaCardSize="medium"
        cardType={"design-media-card"}
        showAttachmentAsPost={false}
        mediaCardClasses={classes.mediaCardWrapper}
      />
    </div>
  );
};
const AttachmentReplyContainerV2 = props => {
  const {
    attachments,
    isParentMessageDeleted,
    isMessagingWidget = false,
    isSidePane = false,
  } = props;
  const noOfAttachments = _.size(attachments);

  if (!noOfAttachments || isParentMessageDeleted) return null;

  const moreAttachmentCountClass = classNames(
    "text-body-2",
    classes.moreAttachmentCount
  );

  const firstAttachment = _.first(attachments);
  const isInteractiveAttachment =
    _.size(attachments) === 1 &&
    _.includes(
      [INTERACTION_TYPE.POLL, INTERACTION_TYPE.FORM],
      _.get(firstAttachment, "type")
    );

  if (isInteractiveAttachment) {
    return <InteractiveAttachmentContainer attachment={firstAttachment} />;
  }

  //we will only show first 3 attachments rest we will show numbers
  const first3Attachments = _.slice(attachments, 0, 3);

  const getAttachmentContainerStyle = () => {
    if (isMessagingWidget || isSidePane) {
      return style.attachmentContainerWidgetStyle;
    }
    return getAttachmentGrid({ noOfAttachments: _.min([noOfAttachments, 3]) });
  };

  return (
    <div className={classes.attachmentReplyContainer}>
      <AttachmentList
        value={first3Attachments}
        mode="view"
        mediaCardSize={"small"}
        cardType={"design-media-card"}
        mediaCardClasses={classes.mediaCardWrapper}
        isDownloadEnabled={false}
        attachmentContainerStyle={getAttachmentContainerStyle()}
        containerStyle={style.containerStyle}
        horizontalCardStyle={style.horizontalCardStyle}
        showAttachmentAsPost={false}
      />
      {noOfAttachments > 3 ? (
        <div className={moreAttachmentCountClass}>
          {`+${noOfAttachments - 3}`}
        </div>
      ) : null}
    </div>
  );
};

const mapAttachmentTitleToType = {
  [ATTACHMENT_TYPES.IMAGE]: "common:photo",
  [ATTACHMENT_TYPES.VIDEO]: "common:video",
  [ATTACHMENT_TYPES.FILE]: "common:file",
  [ATTACHMENT_TYPES.LINK]: "common:link",
};
const FILE_LINK_TYPES = [ATTACHMENT_TYPES.FILE, ATTACHMENT_TYPES.LINK];

const LabelText = props => {
  const { label, isDeleted, totalAttachments, singleAttachment } = props;
  const { t } = useI18n(["common"]);

  if (isDeleted) return t("common:message_was_deleted");
  if (label) {
    const parsedLabel = getCommentDisplayContent(label, {
      getHtml: item => `@${item}`, // without highlighting in parent message
      renderLinksAsAnchor: false,
    });
    return (
      <span
        className={classes.labelText}
        dangerouslySetInnerHTML={{ __html: parsedLabel }}
      />
    );
  }

  // If label is not present meaning, this is a attachments only message.

  // In an Ideal scenario, this should never happen, just a fallback
  if (!totalAttachments) return null;
  if (totalAttachments > 1) return t("common:attachments");

  if (
    _.includes(FILE_LINK_TYPES, singleAttachment.type) &&
    singleAttachment.name
  ) {
    return singleAttachment.name;
  }

  const attachmentTitle = _.get(
    mapAttachmentTitleToType,
    singleAttachment.type,
    null
  );

  return attachmentTitle ? t(attachmentTitle) : null;
};

const style = {
  attachmentContainerWidgetStyle: {
    display: "flex",
    columnGap: "8px",
  },
  containerStyle: {
    flexShrink: "1",
  },
  horizontalCardStyle: {
    maxWidth: "210px",
    height: "32px",
  },
  lineFixHeight: {
    height: "16px",
  },
  lineStretchHight: { alignSelf: "stretch" },
};

const ReplyContainerV2 = props => {
  const {
    createdBy,
    label,
    isParentMessageDeleted,
    attachments,
    isFromChatBar,
    isParentMessageEdited = false,
    defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    labelCustomClass = {},
    showUserName = false,
    parentMessageType,
  } = props;

  const isForwardedMessage = _.isEqual(
    parentMessageType,
    MESSAGE_TYPE.FORWARDED_MESSAGE
  );

  const { t } = useI18n(["common", "integration"]);
  const { screenMode, isPreviewMessagesModal } = useChatOptions();

  const replyToTextClass = classNames("text-label-4", classes.replyToText);
  const deletedMessageClass = classNames(
    "text-label-4",
    classes.deletedMessage
  );
  const isMessagingWidget = getIsMessagingWidget({ screenMode });
  const isSidePane = getIsSidePaneMode({ screenMode });

  const condToWrapMessage =
    (isMessagingWidget || isSidePane) &&
    (isParentMessageDeleted || (_.size(attachments) && !label));

  const upperMessageContainerClass = classNames(classes.upperMessageContainer, {
    [classes.messageContainerWrap]: condToWrapMessage,
  });
  const replyContainerV2Class = classNames(classes.replyContainerV2, {
    [classes.parentMessagePadding]: isFromChatBar,
  });
  const editMessageClass = classNames("text-body-3", classes.editedMessage);
  const labelClasses = classNames(classes.labelClassV2, {
    [classes.labelHoveredV2]: !isPreviewMessagesModal,
    [labelCustomClass]: true,
  });

  const userName = getFormattedNameForUserType({
    user: createdBy,
    orderBy: defaultNameSortPreference,
    defaultStudentNameOrderPreference,
    isFormattedNameV2: true,
  });

  return (
    <div className={replyContainerV2Class}>
      <div
        className={classes.borderLeft}
        style={
          (label && _.size(attachments)) ||
          condToWrapMessage ||
          isForwardedMessage
            ? style.lineStretchHight
            : style.lineFixHeight
        }
      ></div>
      {isForwardedMessage ? (
        <div className={classes.repliedMessage}>
          <div className={upperMessageContainerClass}>
            <Avatar
              shade="light"
              size="xxx-small"
              src={_.get(createdBy, "profileImage", null)}
              shape="square"
              color={getAvatarColor(_.get(createdBy, "id", null))}
              name={`${_.get(createdBy, "firstName")} ${_.get(
                createdBy,
                "lastName"
              )}`}
            />
            <div className="text-label-4">{userName}</div>
          </div>
          {label ? (
            <div className="text-body-2">
              <LabelText label={label} />
            </div>
          ) : null}
          {_.size(attachments) ? (
            <div className={classes.replyMessageBottomContainer}>
              <ForwardMessageAttachmentContainer attachments={attachments} />
            </div>
          ) : null}
        </div>
      ) : (
        <div className={classes.repliedMessage}>
          <div className={upperMessageContainerClass}>
            <div className={classes.userAvatar}>
              <Avatar
                shade="light"
                size="xxx-small"
                src={_.get(createdBy, "profileImage", null)}
                shape="square"
                color={getAvatarColor(_.get(createdBy, "id", null))}
                name={`${_.get(createdBy, "firstName")} ${_.get(
                  createdBy,
                  "lastName"
                )}`}
              />
            </div>
            <div className={replyToTextClass}>
              {isFromChatBar
                ? t("common:replying_to_user", {
                    label: userName,
                  })
                : t("common:reply_to_user", {
                    label: userName,
                  })}
            </div>

            {isParentMessageDeleted ? (
              <div className={deletedMessageClass}>
                {`[${t("common:deleted_message")}]`}
              </div>
            ) : label ? (
              <div className={labelClasses}>
                <LabelText label={label} />
              </div>
            ) : (
              <AttachmentReplyContainerV2
                attachments={attachments}
                isParentMessageDeleted={isParentMessageDeleted}
                isMessagingWidget={isMessagingWidget}
                isSidePane={isSidePane}
              />
            )}

            {isParentMessageEdited && !isParentMessageDeleted ? (
              <div className={editMessageClass}>{`(${t(
                "integration:edited"
              )})`}</div>
            ) : null}
          </div>

          {label && _.size(attachments) && !isParentMessageDeleted ? (
            <div className={classes.replyMessageBottomContainer}>
              {isForwardedMessage ? (
                <ForwardMessageAttachmentContainer attachments={attachments} />
              ) : (
                <AttachmentReplyContainerV2
                  attachments={attachments}
                  isMessagingWidget={isMessagingWidget}
                  isSidePane={isSidePane}
                />
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  defaultNameSortPreference: getDefaultNameSortPreference(state),
  defaultStudentNameOrderPreference: getDefaultStudentNameOrderPreference(
    state
  ),
});
export default connect(mapStateToProps, null)(ReplyContainerV2);
